import { useEffect } from 'react';
import { ShoppingBag, Clock, XCircle, ChevronRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { get_dashboard_index_data } from '../../store/reducers/dashboardReducer';
import { useCurrency } from '../../context/CurrencyContext';

const STATUS_COLOR = {
    paid:      'text-green-600',
    pending:   'text-orange-500',
    cancelled: 'text-red-500',
    placed:    'text-blue-500',
    warehouse: 'text-purple-500',
    delivered: 'text-green-600',
};

const StatCard = ({ icon: Icon, label, value, bg, color }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        </div>
    </div>
);

const OrderCard = ({ order, onPay, formatBase }) => {
    const products = order.products || [];
    const statusCls = STATUS_COLOR[order.delivery_status?.toLowerCase()] || 'text-gray-500';

    return (
        <div className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <span className="text-xs text-gray-500">
                    Order <span className="font-mono font-medium text-gray-700">#{order._id?.slice(-8).toUpperCase()}</span>
                </span>
                <span className={`text-xs font-semibold capitalize ${statusCls}`}>
                    {order.delivery_status}
                </span>
            </div>

            {/* Products row */}
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex gap-2 flex-shrink-0">
                    {products.slice(0, 3).map((p, i) => (
                        <img
                            key={i}
                            src={p.images?.[0]}
                            alt={p.name}
                            className="w-16 h-16 object-cover rounded border border-gray-200"
                        />
                    ))}
                </div>
                <div className="flex-1 min-w-0">
                    {products[0] && (
                        <p className="text-sm text-gray-800 truncate">{products[0].name}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                        {products.length} item{products.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatBase(order.price)}</p>
                    <span
                        className={`text-[11px] font-medium mt-0.5 block capitalize ${
                            order.payment_status === 'paid' ? 'text-green-600' : 'text-orange-500'
                        }`}
                    >
                        {order.payment_status}
                    </span>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                <Link
                    to={`/dashboard/order/details/${order._id}`}
                    className="px-4 py-1.5 text-xs font-medium border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    View Details
                </Link>
                {order.payment_status !== 'paid' && (
                    <button
                        onClick={() => onPay(order)}
                        className="px-4 py-1.5 text-xs font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-full transition-colors"
                    >
                        Pay Now
                    </button>
                )}
            </div>
        </div>
    );
};

const Index = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.auth);
    const { recentOrders, totalOrder, pendingOrder, cancelledOrder } = useSelector(state => state.dashboard);
    const { formatBase } = useCurrency();

    useEffect(() => {
        dispatch(get_dashboard_index_data(userInfo.id));
    }, []);

    const redirect = (ord) => {
        let items = 0;
        for (let i = 0; i < ord.products.length; i++) {
            items += ord.products[i].quantity;
        }
        navigate('/payment', { state: { price: ord.price, items, orderId: ord._id } });
    };

    return (
        <div className="space-y-3">
            {/* Stats */}
            <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
                <StatCard icon={ShoppingBag} label="Total Orders"     value={totalOrder}    bg="bg-blue-50"   color="text-blue-500" />
                <StatCard icon={Clock}        label="Pending Orders"   value={pendingOrder}  bg="bg-orange-50" color="text-orange-500" />
                <StatCard icon={XCircle}      label="Cancelled Orders" value={cancelledOrder} bg="bg-red-50"   color="text-red-500" />
            </div>

            {/* Recent orders */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-800">Recent Orders</h2>
                    <Link
                        to="/dashboard/my-orders"
                        className="flex items-center gap-0.5 text-xs text-orange-500 hover:text-orange-600 font-medium"
                    >
                        View All <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                {recentOrders.length > 0 ? (
                    recentOrders.map(order => (
                        <OrderCard
                            key={order._id}
                            order={order}
                            onPay={redirect}
                            formatBase={formatBase}
                        />
                    ))
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center py-14">
                        <ShoppingBag className="w-10 h-10 text-gray-300 mb-2" />
                        <p className="text-sm font-medium text-gray-500">No orders yet</p>
                        <p className="text-xs text-gray-400 mt-1">Your recent orders will appear here</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Index;
